import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROLES, getNavigationForRole } from '../config/roles';

const ProtectedRoute = ({ children }) => {
  const { currentUser, loading, logout } = useAuth();
  const location = useLocation();

  // Show loading while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // If user is not logged in, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user's email is not verified, redirect to login
  if (!currentUser.emailVerified) {
    return <Navigate to="/login" replace />;
  }

  // Only explicitly active roles may enter the application. Associate and
  // viewer are reserved, and an unexpected role must also fail closed.
  const activeRoles = [ROLES.ADMIN, ROLES.BILLING, ROLES.THERAPIST, ROLES.CLIENT];
  if (!activeRoles.includes(currentUser.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900">Account access is not active</h1>
          <p className="mt-3 text-sm leading-relaxed text-gray-600">
            This role is reserved for a future workflow and currently has no access to client information.
            Please contact the practice administrator if your role should be updated.
          </p>
          <button
            type="button"
            onClick={logout}
            className="mt-6 rounded-lg bg-brand-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-400"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Route restrictions based on role
  if (currentUser?.role) {
    const allowedPaths = getNavigationForRole(currentUser.role).map(item => item.path);
    allowedPaths.push('/'); // Allow home page
    
    // Apply role-specific restrictions
    if (!allowedPaths.includes(location.pathname)) {
      
      // Redirect to appropriate default page based on role
      switch (currentUser.role) {
        case ROLES.ADMIN:
          return <Navigate to="/my-clients" replace />;
        case ROLES.BILLING:
          return <Navigate to="/billing" replace />;
        case ROLES.THERAPIST:
          return <Navigate to="/my-clients" replace />;
        case ROLES.CLIENT:
          return <Navigate to="/client-interface" replace />;
        default:
          return <Navigate to="/client-interface" replace />;
      }
    }
  }

  return children;
};

export default ProtectedRoute;
